import { PublicClientApplication, type Configuration } from '@azure/msal-browser';

// Runtime config injected by the api at /config.js (see apps/admin-api/src/server.ts).
// In dev the api isn't necessarily on the same origin, so we also fall back to
// VITE_ENTRA_* build-time env vars if window.APP_CONFIG isn't populated.
type AppConfig = {
  entra: { tenantId: string; spaClientId: string; apiAudience: string };
  environment: string;
  /** True only in production — controls whether the SPA enforces role gating. */
  strictRoles: boolean;
};

export const appConfig: AppConfig = window.APP_CONFIG ?? {
  entra: { tenantId: '', spaClientId: '', apiAudience: '' },
  environment: 'development',
  strictRoles: false,
};

declare global {
  interface Window {
    APP_CONFIG?: AppConfig;
  }
}

const PLACEHOLDER = '00000000-0000-0000-0000-000000000000';

const runtime = window.APP_CONFIG?.entra;

const tenantId =
  runtime?.tenantId ||
  (import.meta.env.VITE_ENTRA_TENANT_ID as string) ||
  PLACEHOLDER;
const clientId =
  runtime?.spaClientId ||
  (import.meta.env.VITE_ENTRA_SPA_CLIENT_ID as string) ||
  PLACEHOLDER;
const apiAudience =
  runtime?.apiAudience ||
  (import.meta.env.VITE_ENTRA_API_AUDIENCE as string) ||
  `api://${PLACEHOLDER}`;

const apiScope = `${apiAudience}/access_as_user`;

export const msalConfigured =
  tenantId !== PLACEHOLDER && clientId !== PLACEHOLDER && !apiAudience.endsWith(PLACEHOLDER);

if (!msalConfigured && import.meta.env.DEV) {
  console.warn(
    '[msal] Entra config missing — sign-in disabled. Use "Skip auth (dev)" or set window.APP_CONFIG / VITE_ENTRA_*.',
  );
}

const config: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri: window.location.origin,
  },
  cache: {
    // Shared by every tab of the browser, so a link opened in a new tab is
    // signed in already. Sign-out (logoutRedirect) clears it for all tabs.
    cacheLocation: 'localStorage',
  },
};

export const msalInstance = new PublicClientApplication(config);

export const loginRequest = {
  scopes: ['openid', 'profile', apiScope],
};

export const apiRequest = {
  scopes: [apiScope],
};
